import React, { useState, FC, useRef, RefObject, useEffect } from 'react';
import {
    Button,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Alert,
    AlertTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { BalanceDomino, BalanceDominoUpdate } from 'repository/timekeeper/@types/BalanceDomino';
import { TipoCascadaEnum } from 'repository/timekeeper/@types/TypeCascadeEnum';
import { BalanceDominoForm } from './BalanceDominoForm';


interface UpdateProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: BalanceDomino[];
    rowToEdit: BalanceDomino;
    reloading: () => void;
    cleanSelected: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

export const UpdateBalanceDomino: FC<UpdateProps> = ({ open, setOpen, rows, rowToEdit, reloading, cleanSelected }) => {

    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState<BalanceDominoUpdate>({
        balance_domino_id: rowToEdit?.balance_domino_id || 0,
        name: rowToEdit?.name || '',
        description: rowToEdit?.description || null,
        type_accrual: rowToEdit?.type_accrual || TipoAcumulacionEnum.HORAS,
        fixed_amount: rowToEdit?.fixed_amount || 0,
        percentage_cascade: rowToEdit?.percentage_cascade || 0,
        type_cascade: rowToEdit?.type_cascade || TipoCascadaEnum.REDUCIR_A_CERO,
        date_pattern_id: rowToEdit?.date_pattern_id || 0,
        accrual_code_id: rowToEdit?.accrual_code_id || null,
        cascade_orders: rowToEdit?.cascade_orders || [],
    });

    const [loading, setLoading] = useState(false)
    const [alerts, setAlerts] = useState(false)
    const [datePatterns, setDatePatterns] = useState<any[]>([])
    const [accrualCodes, setAccrualCodes] = useState<any[]>([])

    useEffect(() => {
        const loadData = async () => {
            try {
                const [datePatternsList, accrualCodesList] = await Promise.all([
                    timekeeperService.date_pattern.getAll(),
                    timekeeperService.accrual_code.getAll()
                ]);
                setDatePatterns(datePatternsList);
                setAccrualCodes(accrualCodesList);
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };
        if (open) {
            loadData();
        }
    }, [open]);

    useEffect(() => {
        if (rowToEdit) {
            setData({
                balance_domino_id: rowToEdit.balance_domino_id,
                name: rowToEdit.name,
                description: rowToEdit.description,
                type_accrual: rowToEdit.type_accrual,
                fixed_amount: rowToEdit.fixed_amount,
                percentage_cascade: rowToEdit.percentage_cascade,
                type_cascade: rowToEdit.type_cascade,
                date_pattern_id: rowToEdit.date_pattern_id,
                accrual_code_id: rowToEdit.accrual_code_id,
                cascade_orders: rowToEdit.cascade_orders || [],
            });
        }
    }, [rowToEdit]);

    const changes = () => {
        if (
            data.name !== ''
        ) {
            setAlerts(true)
        } else {
            setOpen(false);
        }
    }

    const closeForm = () => {
        setOpen(false);
        setAlerts(false);
        setLoading(false);
    }


    const update = async () => {
        setLoading(true);

        try {
            const balanceDominoId = rowToEdit?.balance_domino_id;
            if (balanceDominoId === undefined || balanceDominoId === null) {
                enqueueSnackbar(t('No se encontró el identificador del balance dominó a actualizar'), { variant: 'error' });
                setLoading(false);
                return;
            }

            console.log('Data to update:', data);
            await timekeeperService.balance_domino.update(balanceDominoId, data);
            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.update.success'), { variant: 'success' });
            closeForm();
            cleanSelected();
            reloading();

        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('accruals.balance_domino.dialog.alerts.update.error'), { variant: 'error' });
            setLoading(false);
        }
    };

    const handleDataChange = (newData: any) => {
        setData({
            ...newData,
            balance_domino_id: data.balance_domino_id,
        });
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                changes()
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="md"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.balance_domino.dialog.titles.update')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => changes()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                {alerts && (
                    <Alert
                        variant='filled'
                        severity="warning"
                        sx={{ mt: 2 }}
                        action={
                            <>
                                <Button onClick={() => setAlerts(false)} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.yes')}
                                </Button>

                                <Button onClick={() => closeForm()} color="inherit" size="small" variant='text' disableElevation>
                                    {t('general.alerts.unsaved_changes.buttons.no')}
                                </Button>
                            </>
                        }
                    >
                        <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                    </Alert>
                )}
            </Box>
            <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                    <BalanceDominoForm
                        data={data}
                        setData={handleDataChange}
                        rows={rows}
                        rowToEdit={rowToEdit}
                        t={t}
                        datePatterns={datePatterns}
                        accrualCodes={accrualCodes}
                        mode="update"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={() => changes()}>
                    {t('general.buttons.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={() => update()} loading={loading}>
                    {t('general.buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};